import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { goPlace } from '../lib/router'

/**
 * A venue's name as its own tap target (a1-28): the place page, one tap away
 * from wherever the name is printed.
 *
 * In a row the name sits inside `ui/Row`'s press target, which is one
 * `<button>` — and an anchor or a second button inside a button is not valid
 * HTML and does not take a tap reliably. So there it is a `<span
 * role="link">`, with its own keys, and it stops the click so the row does not
 * also open its sheet. Elsewhere (`as="button"`) it is a real button. Its hit
 * area is the box `.venue-link` pads out around the text; the line does not
 * move.
 */
export function VenueLink({ id, as = 'span', className = '', onOpen, children }: {
  id: string
  as?: 'span' | 'button'
  className?: string
  /** Before navigating: the sheet closes itself here. */
  onOpen?: () => void
  children: ReactNode
}) {
  const open = (ev: MouseEvent | KeyboardEvent) => {
    ev.stopPropagation()
    ev.preventDefault()
    onOpen?.()
    goPlace(id)
  }
  const cls = `venue-link ${className}`.trim()
  if (as === 'button') {
    return <button type="button" className={cls} onClick={open}>{children}</button>
  }
  return (
    <span
      role="link" tabIndex={0} className={cls}
      onClick={open}
      onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') open(ev) }}
    >
      {children}
    </span>
  )
}
